import type { Faq } from "@/components/site/InsuranceProse";
import { EN_PLANS, type PlanRow } from "@/lib/insurance-va-plans";

/**
 * Spanish translation of the Virginia medication-coverage hub data
 * (plan-by-plan criteria and the hub FAQ). Kept in one module so the Spanish
 * insurance page and any future Spanish surface stay in sync.
 *
 * Rows are *overrides*: anything omitted (or left blank) falls back to the
 * English row at the same position, so an untranslated cell still renders
 * real content instead of an empty box.
 */
export type EsPlanRow = PlanRow;

type EsPlanOverride = Partial<PlanRow>;

const ES_PLAN_OVERRIDES: EsPlanOverride[] = [
  {
    plan: "Medicaid de Virginia (Cardinal Care)",
    bmi: "Más de 40 — o más de 37 con colesterol alto, presión alta o diabetes tipo 2",
    bmiNote:
      "El IMC es el índice de masa corporal. Cardinal Care usa umbrales más altos y exige una enfermedad relacionada con el peso para el umbral menor.",
    lifestyle: "Consejería nutricional y programa de actividad física",
    lifestyleNote:
      "Por lo general piden constancia de un programa o profesional reconocido, no solo un registro personal.",
    step: "Sí — primero un medicamento que no sea GLP-1",
    stepNote:
      "La terapia escalonada significa probar primero un medicamento más antiguo o más económico y demostrar que no funcionó o no se toleró.",
    initial: "6 meses",
    initialNote:
      "La aprobación inicial suele darse por 6 meses; después hay que comprobar el beneficio para renovar.",
    renewal: "Al menos 5% de pérdida de peso",
    renewalNote:
      "Debe perder el 5% de su peso inicial para renovar; es el punto de referencia habitual entre aseguradoras.",
  },
  {
    plan: "Planes de empleados estatales de Virginia (COVA)",
    bmi: "35 o más (subió desde 27/30 el 1 de julio de 2026)",
    bmiNote:
      "COVA elevó su umbral de IMC en 2026. Algunos planes COVA de empleadores específicos pueden tener reglas distintas.",
    lifestyle: "Aplican las guías estándar de autorización previa",
    step: "No se especifica en los materiales del plan",
    initial: "Según la autorización previa",
    initialNote: "La cobertura se decide caso por caso mediante la autorización previa del plan.",
    renewal: "Según la autorización previa",
  },
  {
    plan: "Sentara Health Plans",
    bmi: "30 — o 27 con una enfermedad que califique",
    bmiNote:
      "Una enfermedad que califica es un problema de salud relacionado con el peso, como diabetes, presión alta o apnea del sueño.",
    lifestyle: "Plan de pérdida de peso iniciado en los últimos 6 meses y aún activo",
    lifestyleNote:
      "El plan debe haber comenzado recientemente y seguir activo cuando presente la solicitud.",
    step: "No",
    stepNote:
      "Los criterios publicados de Sentara no exigen actualmente probar otro medicamento antes.",
    initial: "7 meses",
    initialNote:
      "Sentara suele autorizar un suministro inicial de 7 meses antes de pedir documentación de renovación.",
    renewal: "Al menos 5% de pérdida de peso, luego mantenimiento",
    renewalNote:
      "Tras comprobar el 5% de pérdida, la aprobación puede continuar bajo un esquema de mantenimiento.",
  },
  {
    plan: "UnitedHealthcare (si el empleador elige la cobertura)",
    bmi: "30 — o 27 con una enfermedad que califique",
    bmiNote:
      "Los planes de UnitedHealthcare por empleador solo cubren medicamentos de pérdida de peso si el empleador lo incluye.",
    lifestyle: "Junto con cambios de estilo de vida",
    step: "No",
    initial: "Saxenda 4 meses; Wegovy 5 meses; Zepbound 6 meses",
    initialNote:
      "La duración de la aprobación inicial varía según el medicamento: Saxenda 4 meses, Wegovy 5 meses, Zepbound 6 meses.",
    renewal: "4% con Saxenda; 5% con Wegovy y Zepbound",
    renewalNote:
      "Saxenda exige al menos 4% de pérdida de peso; Wegovy y Zepbound exigen al menos 5%.",
  },
  {
    plan: "Cigna",
    bmi: "30 — o 27 con una enfermedad que califique",
    lifestyle: "Al menos 3 meses de cambios de conducta y alimentación",
    lifestyleNote:
      "Los cambios deben estar documentados por un profesional; una dieta reportada por el paciente no suele contar.",
    step: "No",
    initial: "Zepbound y Wegovy 8 meses; Saxenda 4 meses",
    initialNote:
      "Cigna autoriza Zepbound y Wegovy por 8 meses al inicio, y Saxenda por 4 meses.",
    renewal: "5% con Zepbound y Wegovy; 4% con Saxenda",
    renewalNote:
      "Los umbrales de renovación cambian según el medicamento: 5% para Zepbound y Wegovy, 4% para Saxenda.",
  },
  {
    plan: "TRICARE",
    bmi: "30 — o de 27 a 29 con una enfermedad que califique",
    bmiNote:
      "TRICARE permite un rango de IMC menor (27 a 29) cuando hay una enfermedad que califica documentada.",
    lifestyle: "Al menos 6 meses documentados en su expediente",
    lifestyleNote:
      "Se requieren seis meses seguidos de dieta, ejercicio y cambios de conducta documentados.",
    step: "Sí — 3 meses de prueba con un medicamento genérico",
    stepNote:
      "Debe probar un medicamento genérico para bajar de peso durante tres meses antes de que se considere un GLP-1.",
    initial: "12 meses",
    initialNote:
      "La autorización inicial de TRICARE suele ser de 12 meses, más larga que la de la mayoría de los planes comerciales.",
    renewal: "Al menos 5% de pérdida de peso",
  },
  {
    plan: "Programa puente de GLP-1 para Medicare",
    bmi: "Se registra el IMC; solo para pérdida de peso, sin diagnóstico elegible en la Parte D",
    bmiNote:
      "Es un programa temporal de ayuda del fabricante, no cobertura tradicional de Medicare Parte D.",
    lifestyle: "Nutrición estructurada y actividad física",
    step: "No",
    initial: "Vigente hasta el 31 de diciembre de 2027",
    initialNote:
      "El programa termina el 31 de diciembre de 2027, salvo que el fabricante lo extienda.",
    renewal: "Copago de $50 al mes",
    renewalNote: "El copago de $50 al mes aplica mientras esté inscrito en el programa puente.",
  },
  {
    plan: "Planes del mercado (ACA) y de grupos pequeños en Virginia",
    bmi: "Por lo general no cubiertos — el plan de referencia del estado excluye estos medicamentos",
    bmiNote:
      "El plan de beneficios esenciales de Virginia no incluye medicamentos contra la obesidad, y la mayoría de los planes ACA siguen esa regla.",
    lifestyle: "—",
    step: "—",
    initial: "—",
    renewal: "—",
  },
];

/** English row values with the Spanish overrides applied field by field. */
function mergePlan(en: PlanRow, es: EsPlanOverride | undefined): PlanRow {
  const out: PlanRow = { ...en };
  if (!es) return out;
  for (const key of Object.keys(en) as (keyof PlanRow)[]) {
    const value = es[key];
    if (typeof value === "string" && value.trim() !== "") out[key] = value;
  }
  // Notes only present on the Spanish side still need to render.
  for (const key of Object.keys(es) as (keyof PlanRow)[]) {
    const value = es[key];
    if (typeof value === "string" && value.trim() !== "") out[key] = value;
  }
  return out;
}

/**
 * Spanish plan rows. Always the same length and order as the English grid,
 * so a missing translation degrades to the English cell rather than to a gap.
 */
export const ES_PLANS: PlanRow[] = EN_PLANS.map((en, i) =>
  mergePlan(en, ES_PLAN_OVERRIDES[i]),
);

export const ES_FAQS: Faq[] = [
  {
    q: "¿Mi seguro cubre Zepbound o Wegovy en Virginia?",
    a: "Depende de dos puntos de control distintos. Primero: ¿su plan cubre medicamentos para bajar de peso en general? Muchos planes de empleadores en Virginia, y todos los planes individuales y de grupos pequeños del mercado ACA, los excluyen por completo. Segundo: si la categoría sí está cubierta, ¿cumple usted los criterios clínicos de la autorización previa? Normalmente piden un IMC de 30 o más, o de 27 o más con una enfermedad relacionada con el peso, además de constancia de cambios de estilo de vida. Pregunte primero por la exclusión, porque ningún documento vence a una exclusión del beneficio.",
  },
  {
    q: "Mi plan excluye los medicamentos para bajar de peso. ¿Hay otra manera de obtener cobertura?",
    a: "Sí. Algunos pacientes tienen una puerta alterna. Como la misma molécula se vende con distintas marcas para distintas enfermedades, un diagnóstico documentado de diabetes tipo 2, apnea obstructiva del sueño, enfermedad cardiovascular establecida o hígado graso con inflamación (MASH) puede hacer que el medicamento quede cubierto bajo otra indicación aprobada.",
  },
  {
    q: "¿Medicaid de Virginia cubre medicamentos para bajar de peso?",
    a: "Medicaid de Virginia y los planes de atención administrada Anthem HealthKeepers Plus usan un proceso de autorización de servicio para estos medicamentos. La cobertura existe, pero es condicional: espere umbrales de IMC, constancia de participación en un programa y reautorizaciones periódicas según el peso perdido. Su médico envía la solicitud con el formulario de manejo de peso de DMAS.",
  },
  {
    q: "¿El plan de empleados estatales de Virginia cubre Zepbound o Wegovy?",
    a: "El plan estatal ha cubierto medicamentos contra la obesidad con autorización previa, pero el Departamento de Recursos Humanos del estado ha endurecido las condiciones conforme suben los costos. Revise los materiales vigentes de inscripción abierta de su año de plan antes de suponer que siguen las reglas del año pasado, y confirme qué medicamentos están en el formulario, no solo cuáles están «cubiertos».",
  },
  {
    q: "¿Medicare cubre Wegovy o Zepbound en Virginia?",
    a: "La Parte D de Medicare sigue excluyendo los medicamentos usados únicamente para bajar de peso. Sí cubre estas mismas moléculas cuando se recetan por una indicación aprobada distinta: semaglutida para reducir el riesgo cardiovascular, tirzepatida para apnea del sueño de moderada a grave, o las versiones para diabetes tipo 2. Por eso un estudio del sueño o una hemoglobina A1c pueden cambiar la respuesta por completo.",
  },
  {
    q: "¿Cuánto tarda la autorización previa en Virginia?",
    a: "La mayoría de los planes comerciales resuelve una autorización previa de farmacia en pocos días hábiles una vez que reciben el expediente completo; las solicitudes urgentes son más rápidas. Las demoras casi siempre se deben a documentos faltantes —historial de peso, IMC o constancia del programa de estilo de vida— y no a la lentitud del revisor.",
  },
  {
    q: "Me negaron la autorización previa. ¿Qué derechos de apelación tengo?",
    a: "Lea la carta de negación y localice el criterio exacto que citan. Si es un criterio clínico, una apelación interna con la documentación faltante suele tener éxito. Si la apelación interna falla, los planes totalmente asegurados de Virginia pasan a revisión externa ante la Oficina de Seguros de Virginia (Bureau of Insurance); los planes autofinanciados de empleadores siguen la revisión externa federal de ERISA. Si la negación es una exclusión del beneficio y no un criterio clínico, las apelaciones rara vez funcionan: conviene buscar la indicación alterna.",
  },
  {
    q: "Vivo en Virginia, pero el plan de mi empleador es de otro estado. ¿Qué reglas aplican?",
    a: "Las condiciones de su beneficio siguen el documento del plan, no su dirección. Un plan autofinanciado con sede en otro estado aplica su propio formulario y sus exclusiones aunque usted viva en Virginia. Pida a Recursos Humanos la descripción resumida del plan y el formulario de farmacia, y revise primero la pregunta de la exclusión.",
  },
  {
    q: "¿Los planes del mercado (ACA) en Virginia cubren medicamentos para bajar de peso?",
    a: "Casi nunca. Los planes individuales y de grupos pequeños que se venden en el mercado de Virginia siguen el plan de referencia estatal de beneficios esenciales, que no incluye medicamentos contra la obesidad. Algunas aseguradoras venden un anexo opcional o un programa aparte de manejo de peso, pero los documentos estándar los excluyen. Si la cobertura de medicamentos le importa, lea la lista de exclusiones de farmacia antes de comparar primas.",
  },
  {
    q: "¿TRICARE cubre Zepbound o Wegovy para familias militares en Virginia?",
    a: "TRICARE sí cubre medicamentos aprobados por la FDA para el manejo del peso a través del beneficio de farmacia de Express Scripts, con autorización previa y requisitos escalonados: normalmente un umbral de IMC documentado y participación en un programa de estilo de vida. Las reglas y los medicamentos preferidos cambian con el formulario, así que pida a su médico confirmar los criterios vigentes antes de empezar.",
  },
  {
    q: "¿La cirugía bariátrica sí está cubierta cuando el medicamento no lo está?",
    a: "Con frecuencia sí, y eso sorprende a muchas personas. Numerosos planes de empleadores en Virginia excluyen los medicamentos contra la obesidad y, aun así, cubren la manga gástrica o el bypass gástrico como beneficio médico, porque la cirugía se cotiza como un procedimiento único y no como un gasto de farmacia permanente. Si le niegan el medicamento por una exclusión, vale la pena revisar su beneficio quirúrgico antes de dar todo por perdido.",
  },
  {
    q: "¿Cuánto cuestan estos medicamentos sin seguro en Virginia?",
    a: "Los programas de pago directo del fabricante y los precios de contado en farmacia han bajado bastante el costo respecto al precio de lista, y la cifra cambia con frecuencia. Revisamos con usted las opciones de pago propio vigentes durante la consulta, para que la comparación se base en los precios de este mes y no en un dato viejo.",
  },
];
